/* ============================================================
   CTS CART PDF
   ------------------------------------------------------------
   Generates a PDF of the current Shopify cart, reusing the
   layout from the old quote PDF (logo, To/Date table, 6-col
   product table, estimated total, page breaks).

   Line items come from the live cart (/cart.js). For each line,
   the product page is fetched and its injected QuoteProductData
   (real part number + spec block) is extracted — same technique
   the configurator used — so specs match the product pages.

   Public API:  window.CartPDF.download()
   Load via theme.liquid:
     {{ 'cts-cart-pdf.js' | asset_url | script_tag }}
   ============================================================ */
(function () {
  'use strict';

  var LOGO_URL = 'https://cdn.shopify.com/s/files/1/0681/2975/1100/files/CTS-favicon.webp?v=1767046530';
  var CONTACT_EMAIL = 'sales@cincinnati-test.com';
  var CONTACT_PHONE = '513-367-6699';

  // Spec labels used to re-split a spec block onto one line per label.
  // Sorted longest-first so compound labels win (e.g. 'Connect Body Size:'
  // matches before 'Body Size:', 'Seal Type:' before 'Type:').
  var SPEC_LABELS = [
    'Method:', 'Type:', 'Body Style:', 'Seal Set:', 'Seal Range:', 'Seal Type:',
    'Seal Grade:', 'Number of Seals:', 'Bore Type:', 'Test Air Connection:',
    'Pilot Air Connection:', 'Construction Materials:', 'Test pressure:',
    'Pilot pressure:', 'Thread Size:', 'Accessory:', 'Body Size:',
    'Lead-in Size:', 'Connect Body Size:', 'ID Sealing Range:', 'OD Sealing Range:'
  ];
  var SPEC_SPLIT_RE = new RegExp(
    '([^\\n])\\s*(' +
    SPEC_LABELS.slice().sort(function (a, b) { return b.length - a.length; })
      .map(function (l) { return l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); })
      .join('|') +
    ')', 'g'
  );

  function normalizeSpec(raw) {
    if (raw && typeof raw !== 'string') raw = JSON.stringify(raw);
    raw = (raw || '').replace(SPEC_SPLIT_RE, '$1\n$2');
    return raw.split(/\r?\n/)
      .map(function (ln) { return ln.trim(); })
      .filter(function (ln) { return ln.length > 0; })
      .join('\n');
  }

  function money(cents) {
    var n = (cents || 0) / 100;
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  var CartPDF = {
    _jspdfLoading: false,
    _busy: false,

    // Ensure jsPDF is loaded, then run cb().
    loadJsPDF: function (cb) {
      if (window.jspdf && window.jspdf.jsPDF) { cb(); return; }
      if (this._jspdfLoading) {
        var t = setInterval(function () {
          if (window.jspdf && window.jspdf.jsPDF) { clearInterval(t); cb(); }
        }, 100);
        return;
      }
      this._jspdfLoading = true;
      var s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = function () { cb(); };
      s.onerror = function () {
        alert('Could not load the PDF generator. Please try again, or contact us directly.');
      };
      document.head.appendChild(s);
    },

    // Load an image URL and convert to a PNG data URL via canvas.
    loadImageAsPng: function (url) {
      return new Promise(function (resolve) {
        if (!url) { resolve(null); return; }
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          try {
            var canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve({ dataUrl: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height });
          } catch (e) {
            resolve(null);
          }
        };
        img.onerror = function () { resolve(null); };
        img.src = url;
      });
    },

    // Extract window.QuoteProductData[<id>] = { ... }; from fetched product
    // page HTML and return the parsed object (or null).
    extractProductData: function (html, productId) {
      try {
        var marker = 'window.QuoteProductData[' + productId + '] =';
        var start = html.indexOf(marker);
        if (start === -1) return null;
        var braceStart = html.indexOf('{', start);
        if (braceStart === -1) return null;
        var depth = 0, end = -1;
        for (var i = braceStart; i < html.length; i++) {
          var ch = html[i];
          if (ch === '{') depth++;
          else if (ch === '}') { depth--; if (depth === 0) { end = i; break; } }
        }
        if (end === -1) return null;
        return (new Function('return (' + html.slice(braceStart, end + 1) + ');'))();
      } catch (err) {
        return null;
      }
    },

    // Fetch the live cart, then enrich each line with real part number + spec
    // block from its product page. Resolves to an array of item objects.
    loadCartItems: function () {
      var self = this;
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var lines = (cart && cart.items) || [];
          // fetch each product page once (dedupe by product id)
          var seen = {};
          var fetches = lines.map(function (line) {
            var pid = line.product_id;
            if (seen[pid]) return seen[pid];
            var handle = line.handle || (line.url ? line.url.split('/products/')[1] : '');
            handle = handle ? handle.split('?')[0] : '';
            seen[pid] = fetch('/products/' + handle, { headers: { 'Accept': 'text/html' } })
              .then(function (res) { return res.ok ? res.text() : ''; })
              .then(function (html) { return self.extractProductData(html, pid); })
              .catch(function () { return null; });
            return seen[pid];
          });
          return Promise.all(fetches).then(function (datas) {
            return lines.map(function (line, idx) {
              var data = datas[idx] || {};
              var props = line.properties || {};
              // The calibrated-leak configurator stores its config in the
              // line item property named "Configuration".
              var config = props.Configuration || props.configuration || props._configuration || '';
              // Title is the part number. Prefer the product-page title we
              // extracted (known reliable), then the cart line's own titles.
              var title = (data && data.title) || line.product_title || line.title || '';
              return {
                title: title,
                partNumber: title,
                partDesc: (data && data.partDesc) || '',
                configuration: config,
                qty: line.quantity || 1,
                price: line.price || 0 // per-unit, in cents
              };
            });
          });
        });
    },

    // Public entry point.
    download: function () {
      var self = this;
      this.loadJsPDF(function () {
        self.loadCartItems().then(function (items) {
          self.build(items);
        }).catch(function (err) {
          console.error('[CartPDF] Failed to build cart PDF.', err);
          alert('Sorry — the cart PDF could not be generated. Please try again.');
        });
      });
    },

    // Build and save the PDF from the given items array.
    build: function (items) {
      var self = this;
      items = items || [];
      this.loadImageAsPng(LOGO_URL).then(function (logoImg) {
        var jsPDF = window.jspdf.jsPDF;
        var doc = new jsPDF({ unit: 'pt', format: 'letter' });
        var pageW = doc.internal.pageSize.getWidth();
        var pageH = doc.internal.pageSize.getHeight();
        var margin = 40;
        var y = margin;

        // ---- Header: logo (left) ----
        // Bigger logo, but height capped so it never crosses the blue line
        // (which is drawn at a fixed y+58 below).
        if (logoImg) {
          var logoW = 64;
          var logoH = logoImg.h ? (logoImg.h / logoImg.w) * logoW : 64;
          var logoMaxH = 50;
          if (logoH > logoMaxH) {
            logoW = logoW * (logoMaxH / logoH);
            logoH = logoMaxH;
          }
          doc.addImage(logoImg.dataUrl, 'PNG', margin, y, logoW, logoH);
        }

        // thin blue line under header
        y += 58;
        doc.setDrawColor(0, 81, 138);
        doc.setLineWidth(1.2);
        doc.line(margin, y, pageW - margin, y);
        doc.setLineWidth(1);

        // ---- NOT AN OFFICIAL QUOTE banner ----
        y += 22;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('THIS IS NOT AN OFFICIAL QUOTE', pageW / 2, y, { align: 'center' });
        doc.setFont('helvetica', 'normal');

        // ---- To / Date table (cart version: no customer block) ----
        y += 20;
        var dateStr = new Date().toLocaleDateString();

        // Single-column info box: Date at top, a blank line, then contact info.
        var boxLines = [];
        boxLines.push({ t: 'Date: ' + dateStr });
        boxLines.push({ t: '' });
        boxLines.push({ t: 'Contact with questions or to request a formal quote:' });
        boxLines.push({ t: 'Email: ' + CONTACT_EMAIL });
        boxLines.push({ t: 'Phone: ' + CONTACT_PHONE });

        var tableX = margin;
        var tableW = pageW - margin * 2;
        var pad = 10;
        var lineH = 13;
        var tableH = boxLines.length * lineH + pad * 2;
        var tableTop = y;

        doc.setDrawColor(180);
        doc.rect(tableX, tableTop, tableW, tableH);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        var ly = tableTop + pad + 9;
        boxLines.forEach(function (ln) {
          if (ln.t) doc.text(ln.t, tableX + pad, ly);
          ly += lineH;
        });

        y = tableTop + tableH + 24;

        // ---- Product table ----
        // Columns: Line | Part Number | Specifications | Qty | List Unit Price | Net
        var cLine = margin;
        var cPart = margin + 34;
        var cDesc = margin + 110;
        var cQty = pageW - margin - 165;
        var cList = pageW - margin - 70;
        var cNet = pageW - margin;
        var specLH = 9.5;

        var footerY = doc.internal.pageSize.getHeight() - 32;
        var contTopMargin = margin + 30;

        function drawTableHeader(topY) {
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0);
          doc.text('Line', cLine, topY);
          doc.text('Part Number', cPart, topY);
          doc.text('Specifications', cDesc, topY);
          doc.text('Qty', cQty, topY, { align: 'right' });
          doc.text('List Unit Price', cList, topY, { align: 'right' });
          doc.text('Net Price', cNet, topY, { align: 'right' });
          doc.setDrawColor(150);
          doc.setLineWidth(0.5);
          doc.line(margin, topY + 4, pageW - margin, topY + 4);
          doc.setLineWidth(1);
          doc.setFont('helvetica', 'normal');
        }

        drawTableHeader(y);
        y += 16;

        var estTotal = 0;

        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');

          // Description: configuration if present, else the spec block
          var descSource = it.configuration && String(it.configuration).trim()
            ? it.configuration
            : it.partDesc;
          var specClean = normalizeSpec(descSource);
          var specLines = doc.splitTextToSize(specClean || '-', cQty - cDesc - 30);

          var lineTotal = (it.price || 0) * (it.qty || 1);
          estTotal += lineTotal;

          var maxLines = Math.max(Array.isArray(specLines) ? specLines.length : 1, 1);
          var rowHeight = (maxLines - 1) * specLH + 4 + 22;

          if (y + rowHeight > footerY) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0);
            doc.text('Continued on next page', pageW / 2, footerY + 14, { align: 'center' });
            doc.addPage();
            y = contTopMargin;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            drawTableHeader(y);
            y += 16;
          }

          var rowTop = y;
          doc.text(String(i + 1), cLine, y);
          doc.text(it.partNumber || '-', cPart, y);

          var sy = y;
          for (var s = 0; s < specLines.length; s++) {
            doc.text(specLines[s], cDesc, sy);
            sy += specLH;
          }

          doc.text(String(it.qty || 1), cQty, y, { align: 'right' });
          doc.text(money(it.price) + ' / 1', cList, y, { align: 'right' });
          doc.text(money(lineTotal), cNet, y, { align: 'right' });

          var lastLineBottom = Math.max(sy - specLH, y);
          y = lastLineBottom + 22;

          doc.setDrawColor(225);
          doc.setLineWidth(0.5);
          doc.line(margin, y - 13, pageW - margin, y - 13);
          doc.setLineWidth(1);
        }

        // ---- Estimated total ----
        if (y > 720) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0);
          doc.text('Continued on next page', pageW / 2, footerY + 14, { align: 'center' });
          doc.addPage();
          y = contTopMargin;
        }
        y += 10;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Estimated Total:', cList, y, { align: 'right' });
        doc.text(money(estTotal), cNet, y, { align: 'right' });
        doc.setFont('helvetica', 'normal');

        // Note under the estimated total
        y += 16;
        doc.setFontSize(8);
        doc.setTextColor(90);
        doc.text('Note: The estimated total shown does not include applicable taxes or shipping fees.',
          pageW - margin, y, { align: 'right' });
        doc.setTextColor(0);

        // ---- Page X of Y footer ----
        var pageCount = doc.internal.getNumberOfPages();
        for (var p = 1; p <= pageCount; p++) {
          doc.setPage(p);
          doc.setFontSize(8);
          doc.setTextColor(120);
          doc.text('Page ' + p + ' of ' + pageCount, pageW - margin, pageH - 20, { align: 'right' });
          doc.setTextColor(0);
        }

        // ---- Save ----
        var fname = 'cart-' + new Date().toISOString().slice(0, 10) + '.pdf';
        doc.save(fname);
      });
    }
  };

  window.CartPDF = CartPDF;
})();